// src/ChatList.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const ChatList = ({ chats, selectedRoom, onSelectRoom }) => {
    const [chatInfo, setChatInfo] = useState({});

    // Update chatInfo state with new data from WebSocket messages.
    const updateChatInfo = (update) => {
        setChatInfo((prevInfo) => ({
            ...prevInfo,
            [update.room_id]: {
                ...prevInfo[update.room_id],
                unreadCount: update.unreadCount,
                lastMessage: update.lastMessage || prevInfo[update.room_id]?.lastMessage,
                lastActivity: update.lastMessage
                    ? new Date(update.lastMessage.timestamp)
                    : prevInfo[update.room_id]?.lastActivity,
            },
        }));
    };

    // Establish a WebSocket connection for real-time chat list updates.
    useEffect(() => {
        // Build the WebSocket URL dynamically using the environment variable.
        const wsUrl = `${process.env.REACT_APP_API_URL.replace(/^http/, 'ws')}/ws/chat_list/`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            console.log('✅ Connected to chat list updates');
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('📩 Received chat list update:', data);
                updateChatInfo(data);
            } catch (error) {
                console.error('❌ Error parsing WebSocket message:', error);
            }
        };

        ws.onerror = (error) => {
            console.error('❌ Chat list WebSocket error:', error);
        };

        // Cleanup on unmount
        return () => {
            ws.close();
        };
    }, []);

    // Fetch initial chat info for each room.
    useEffect(() => {
        const fetchChatInfo = async () => {
            const info = {};
            for (const roomId of chats) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/chat/messages/?room_id=${roomId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(`Error fetching messages for room ${roomId}:`, response.status, errorText);
                        continue;
                    }

                    const messages = await response.json();

                    if (messages.length > 0) {
                        const lastMessage = messages[messages.length - 1];
                        const unreadCount = messages.filter(
                            (msg) => !msg.is_read && msg.sender !== 'Admin'
                        ).length;

                        info[roomId] = {
                            lastMessage,
                            unreadCount,
                            lastActivity: new Date(lastMessage.timestamp),
                            messages,
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching chat info for room ${roomId}:`, error);
                }
            }
            setChatInfo(info);
        };

        if (chats.length > 0) {
            fetchChatInfo();
        }
    }, [chats]);

    // Sort chats: prioritize chats with unread messages and then by last activity.
    const sortedChats = [...chats].sort((a, b) => {
        const aInfo = chatInfo[a];
        const bInfo = chatInfo[b];

        if (aInfo?.unreadCount && !bInfo?.unreadCount) return -1;
        if (!aInfo?.unreadCount && bInfo?.unreadCount) return 1;

        const aTime = aInfo?.lastActivity ? new Date(aInfo.lastActivity).getTime() : 0;
        const bTime = bInfo?.lastActivity ? new Date(bInfo.lastActivity).getTime() : 0;
        return bTime - aTime;
    });

    // Format timestamp into a readable time or date.
    const formatTime = (date) => {
        if (!date) return '';
        const now = new Date();
        const messageDate = new Date(date);
        if (messageDate.toDateString() === now.toDateString()) {
            return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        return messageDate.toLocaleDateString();
    };

    // Check if a chat is active (within the last 5 minutes).
    const isActive = (timestamp) => {
        if (!timestamp) return false;
        return Date.now() - new Date(timestamp) < 5 * 60 * 1000;
    };

    return (
        <div className="w-full md:w-1/3 border-r dark:border-gray-700">
            <div className="p-4 border-b dark:border-gray-700">
                <h3 className="font-semibold text-gray-800 dark:text-white">Active Chats</h3>
            </div>
            <div className="overflow-y-auto h-[calc(600px-64px)]">
                {sortedChats.map((roomId) => {
                    const info = chatInfo[roomId] || {};
                    const active = isActive(info?.lastMessage?.timestamp);
                    return (
                        <div
                            key={roomId}
                            onClick={() => onSelectRoom(roomId)}
                            className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border-b dark:border-gray-700
                ${selectedRoom === roomId ? 'bg-blue-50 dark:bg-gray-600' : ''}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-gray-400'}`} />
                                    <p className="font-medium text-gray-800 dark:text-white">
                                        Visitor {roomId.slice(0, 8)}...
                                    </p>
                                </div>
                                {info?.lastMessage && (
                                    <span className="text-xs text-gray-500">
                                        {formatTime(info.lastMessage.timestamp)}
                                    </span>
                                )}
                            </div>
                            {info?.lastMessage && (
                                <div className="ml-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                                        {info.lastMessage.sender === 'Admin' ? 'You: ' : ''}
                                        {info.lastMessage.message}
                                    </p>
                                </div>
                            )}
                            {info?.unreadCount > 0 && (
                                <div className="mt-1 ml-4">
                                    <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                                        {info.unreadCount} unread
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

ChatList.propTypes = {
    chats: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedRoom: PropTypes.string.isRequired,
    onSelectRoom: PropTypes.func.isRequired,
};

export default ChatList;
